import { AddressSearchResponse } from '@application/components/address-search/AddressSearch';
import { ReactNode, useCallback, useMemo } from 'react';
import useRecentSearches from '../hooks/useRecentSearches';
import useSiteLookup from '../hooks/useSiteLookup';
import SiteLookupContext, { SiteLookupContextProps } from './SiteLookupContext';

type Props = {
  children: ReactNode;
};

const SiteLookupProvider = ({ children }: Props) => {
  const {
    actions: { applyFilters, downloadReport, retrySearch, setDataView, setOpenFilter, updateFilter },
    state: {
      appliedFilters,
      dataView,
      failedRequest,
      filters,
      hasHistoricalAccess,
      hasRealTimeAccess,
      isDownloadingSitesReport,
      maxHistoricalMonths,
      networks,
      openFilter,
      pendingRequest,
      remainingDownloads,
      result,
    },
  } = useSiteLookup();

  const { recentSearches, addRecentSearch } = useRecentSearches();

  // Record every committed address (address box, lat/lon, and map "Search nearby" all flow through here).
  const updateFilterAddress = useCallback(
    (address: AddressSearchResponse) => {
      updateFilter('address')(address);
      addRecentSearch(address);
    },
    [addRecentSearch, updateFilter],
  );

  const contextValue = useMemo<SiteLookupContextProps>(
    () => ({
      appliedFilters,
      applyFilters,
      dataView,
      downloadReport,
      failedRequest,
      filters,
      hasHistoricalAccess,
      isDownloadingSitesReport,
      hasRealTimeAccess,
      maxHistoricalMonths,
      networks,
      openFilter,
      pendingRequest,
      recentSearches,
      remainingDownloads,
      result,
      retrySearch,
      setDataView,
      setOpenFilter,
      updateFilterAddress,
      updateFilterRadius: updateFilter('radius'),
    }),
    [
      appliedFilters,
      applyFilters,
      dataView,
      downloadReport,
      failedRequest,
      filters,
      hasHistoricalAccess,
      hasRealTimeAccess,
      isDownloadingSitesReport,
      maxHistoricalMonths,
      networks,
      openFilter,
      pendingRequest,
      recentSearches,
      remainingDownloads,
      result,
      retrySearch,
      setDataView,
      setOpenFilter,
      updateFilterAddress,
      updateFilter,
    ],
  );

  return <SiteLookupContext.Provider value={contextValue}>{children}</SiteLookupContext.Provider>;
};

export default SiteLookupProvider;
